// Live spotlight tour for VOC — overlays the real app and spotlights real
// elements (matched by CSS selector). Patterned after the LinkedOut tour.
// onEnter steps can fire side-effects (e.g. open the Notes popup) so the
// tour can guide users into actually using a feature, not just point at it.
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const PAD = 10;

export default function SiteTour({ steps, onFinish }) {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const tipRef = useRef(null);
  const [tip, setTip] = useState({ top: 0, left: 0, ready: false });

  const step = steps[i];
  const last = i === steps.length - 1;

  // Fire per-step side-effects (open Notes, etc.) when entering a step.
  useEffect(() => {
    step?.onEnter?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i]);

  // Find the target element (with retries — the modal it lives in may not be
  // mounted yet right after onEnter).
  useEffect(() => {
    let cancelled = false;
    let tries = 0;
    // Only hide the tip while we're hunting for a real target — centered
    // steps have nothing to wait for and should stay visible.
    if (step.selector) setTip((t) => ({ ...t, ready: false }));

    function locate() {
      if (cancelled) return;
      if (!step.selector) { setRect(null); return; }
      const el = document.querySelector(step.selector);
      if (el) {
        el.scrollIntoView({ block: "center", behavior: "auto" });
        requestAnimationFrame(() => {
          if (cancelled) return;
          const r = el.getBoundingClientRect();
          setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        });
        return;
      }
      if (tries++ < 120) requestAnimationFrame(locate);
      else setRect(null); // give up → centered card
    }
    requestAnimationFrame(locate);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i]);

  // Keep spotlight glued to the target on scroll/resize.
  useEffect(() => {
    if (!step.selector) return;
    function reflow() {
      const el = document.querySelector(step.selector);
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    }
    window.addEventListener("scroll", reflow, true);
    window.addEventListener("resize", reflow);
    return () => {
      window.removeEventListener("scroll", reflow, true);
      window.removeEventListener("resize", reflow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i]);

  // Place the tip relative to the spotlight (or center it).
  useLayoutEffect(() => {
    const tipEl = tipRef.current;
    if (!tipEl) return;
    const tw = tipEl.offsetWidth;
    const th = tipEl.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const M = 14;
    if (!rect) {
      setTip({ top: Math.max(M, (vh - th) / 2), left: Math.max(M, (vw - tw) / 2), ready: true });
      return;
    }
    let top, left;
    const place = step.placement || "bottom";
    if (place === "right" && rect.left + rect.width + tw + 16 < vw) {
      left = rect.left + rect.width + 16; top = rect.top;
    } else if (place === "left" && rect.left - tw - 16 > M) {
      left = rect.left - tw - 16; top = rect.top;
    } else if (place === "top" && rect.top - th - 16 > M) {
      top = rect.top - th - 16; left = rect.left;
    } else if (place === "bottom" && rect.top + rect.height + th + 16 < vh) {
      top = rect.top + rect.height + 16; left = rect.left;
    } else {
      // fallback: above if room, else below
      top = rect.top - th - 16 > M ? rect.top - th - 16 : rect.top + rect.height + 16;
      left = rect.left;
    }
    left = Math.min(Math.max(M, left), vw - tw - M);
    top = Math.min(Math.max(M, top), vh - th - M);
    setTip({ top, left, ready: true });
  }, [rect, i, step.placement]);

  function nextStep() { last ? finish() : setI((n) => n + 1); }
  function prevStep() { setI((n) => Math.max(0, n - 1)); }
  function finish() { onFinish?.(); }

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") finish();
      else if (e.key === "ArrowRight" || e.key === "Enter") nextStep();
      else if (e.key === "ArrowLeft") prevStep();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  return createPortal(
    <div className="tour-root" aria-live="polite">
      {rect ? (
        <div
          className="tour-spotlight"
          style={{
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
          }}
        />
      ) : (
        <div className="tour-dim" />
      )}

      <div
        ref={tipRef}
        className={`tour-tip ${rect ? "" : "centered"}`}
        style={{ top: tip.top, left: tip.left, visibility: tip.ready ? "visible" : "hidden" }}
      >
        <div className="tour-step-count">Step {i + 1} of {steps.length}</div>
        <h3 className="tour-title">{step.title}</h3>
        <p className="tour-body">{step.body}</p>
        <div className="tour-actions">
          <button type="button" className="tour-skip" onClick={finish}>Skip tour</button>
          <div className="tour-nav">
            {i > 0 && <button type="button" className="tour-btn ghost" onClick={prevStep}>← Back</button>}
            <button type="button" className="tour-btn" onClick={nextStep}>
              {last ? "Start exploring" : "Next →"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
